/**
 * Detección de la ventana que está espejando las Magic Leap 2.
 *
 * La vista del usuario no puede obtenerse por el USB: el dispositivo no expone
 * vídeo por ese canal. La vía practicable es espejar su pantalla en el equipo,
 * con scrcpy sobre ADB o con la herramienta del fabricante, y capturar esa
 * ventana con `desktopCapturer`.
 *
 * El reconocimiento del título es la parte frágil, y además hay varias
 * herramientas capaces de mostrar esa imagen, que pueden estar abiertas a la
 * vez: en el escritorio pueden convivir ML Hub 3, el ML Hub 2.0 anterior y una
 * ventana de scrcpy. Por eso no basta con quedarse con la primera coincidencia:
 * hay que reconocerlas todas y ordenarlas por preferencia.
 */

/** Fuente de captura tal como la devuelve `screenCapture.getSources()` */
export interface CaptureSource {
  id: string;
  name: string;
}

/** Fuente reconocida, con el origen identificado */
export interface GlassesWindowCandidate extends CaptureSource {
  /** Nombre legible de la herramienta que la muestra */
  tool: string;
  /** Cuanto más bajo, más preferible */
  priority: number;
}

/**
 * Herramientas capaces de mostrar la pantalla de las gafas, en orden de
 * preferencia.
 *
 * Se prefiere scrcpy porque abre una ventana dedicada que contiene solo la
 * imagen del dispositivo. Las ventanas del ML Hub incluyen toda su interfaz
 * alrededor del vídeo, de modo que al compartirlas se comparte también la
 * herramienta; sirven, pero como segunda opción.
 *
 * El título que scrcpy da a su ventana es el modelo declarado por el
 * dispositivo, que en las Magic Leap 2 es exactamente "Magic Leap 2".
 */
const KNOWN_MIRROR_WINDOWS: Array<{ tool: string; pattern: RegExp; priority: number }> = [
  // Ventana dedicada, solo imagen del dispositivo
  { tool: 'scrcpy', pattern: /\bscrcpy\b/i, priority: 10 },

  // scrcpy titula con el modelo del dispositivo. Se exige que el título sea
  // ese y no una frase que lo contenga, para no confundirlo con el Hub.
  { tool: 'Espejo del dispositivo', pattern: /^\s*(magic\s*leap\s*2|magicleap\s*2|ml2)\s*$/i, priority: 20 },

  // Herramienta del fabricante, versión actual
  { tool: 'ML Hub 3', pattern: /magic\s*leap\s*hub\s*3|\bml[\s-]*hub\s*3\b/i, priority: 30 },

  // Herramienta del fabricante, versión anterior, que puede coexistir
  { tool: 'ML Hub 2', pattern: /magic\s*leap\s*hub\s*2|\bml[\s-]*hub\s*2\b/i, priority: 40 },

  // Hub sin número en el título
  { tool: 'ML Hub', pattern: /magic\s*leap\s*hub|\bml[\s-]*hub\b/i, priority: 50 },

  // Nombre anterior del Hub 2.0
  { tool: 'The Lab', pattern: /\bthe\s*lab\b/i, priority: 60 },

  // Cualquier otra ventana que mencione el fabricante o el dispositivo
  { tool: 'Otra ventana de Magic Leap', pattern: /magic\s*leap|magicleap|\bml2\b/i, priority: 90 },
];

/** Patrones por defecto, expuestos para poder revisarlos */
export const GLASSES_WINDOW_PATTERNS: RegExp[] = KNOWN_MIRROR_WINDOWS.map((w) => w.pattern);

/**
 * Patrón adicional configurable en tiempo de ejecución.
 *
 * Si la ventana espejo se titula de una forma que no cubren los patrones por
 * defecto, o se quiere forzar una concreta entre varias, se puede indicar sin
 * recompilar desde la consola de DevTools:
 *
 *   window.__vrWindowPattern = 'nombre-de-mi-ventana'
 */
function getRuntimePattern(): RegExp | null {
  const raw = (window as any).__vrWindowPattern;
  if (!raw || typeof raw !== 'string') return null;

  try {
    return new RegExp(raw, 'i');
  } catch {
    console.warn('[VR] __vrWindowPattern no es una expresión regular válida:', raw);
    return null;
  }
}

/**
 * Devuelve todas las ventanas que pueden estar espejando las gafas, ordenadas
 * de más a menos preferible.
 *
 * Resulta útil incluso cuando solo se va a usar una: saber que hay varias
 * abiertas explica por qué se comparte una y no otra.
 */
export function findGlassesWindowCandidates(sources: CaptureSource[]): GlassesWindowCandidate[] {
  if (!Array.isArray(sources) || sources.length === 0) return [];

  const runtime = getRuntimePattern();
  const candidates: GlassesWindowCandidate[] = [];

  for (const source of sources) {
    const name = source.name ?? '';
    if (!name) continue;

    // El patrón manual gana a cualquier otra consideración
    if (runtime && runtime.test(name)) {
      candidates.push({ id: source.id, name, tool: 'Selección manual', priority: 0 });
      continue;
    }

    const known = KNOWN_MIRROR_WINDOWS.find((w) => w.pattern.test(name));
    if (known) {
      candidates.push({ id: source.id, name, tool: known.tool, priority: known.priority });
    }
  }

  // Orden estable: por preferencia y, a igualdad, por el orden recibido
  return candidates.sort((a, b) => a.priority - b.priority);
}

/**
 * Devuelve la ventana más adecuada para compartir, o `null` si no hay ninguna.
 */
export function matchGlassesWindow(sources: CaptureSource[]): GlassesWindowCandidate | null {
  return findGlassesWindowCandidates(sources)[0] ?? null;
}
